import { Box, Chip, Fade, Typography } from "@mui/material";
import React from "react";
import { ContentResult } from "../types";
import StepNavButtons from "./StepNavButtons";

const STAGE_LABELS: Record<string, string> = {
  tofu: "Awareness",
  mofu: "Consideration",
  bofu: "Decision",
};

interface ContentPlanPreviewStepProps {
  content?: ContentResult;
  onNext: () => void;
  onBack: () => void;
}

export default function ContentPlanPreviewStep({
  content,
  onNext,
  onBack,
}: ContentPlanPreviewStepProps) {
  return (
    <Fade in timeout={600}>
      <Box>
        <Typography variant="overline" color="primary" sx={{ fontWeight: 700 }}>
          Content Engine
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}>
          Your content plan
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
          A funnel-aware content strategy, ready for your AI team to execute.
        </Typography>

        {!content?.pillars?.length ? (
          <Typography color="text.secondary">
            We couldn't generate a content plan this time. You can create one anytime from
            the Content engine.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {content.salesNarrative && (
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: 1 }}>
                {content.salesNarrative}
              </Typography>
            )}
            {content.pillars.map((pillar, i) => (
              <Box
                key={i}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "action.hover",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {pillar.title}
                  </Typography>
                  {pillar.stage && (
                    <Chip
                      label={STAGE_LABELS[pillar.stage] || pillar.stage}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>
                {pillar.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {pillar.description}
                  </Typography>
                )}
                {!!pillar.topics?.length && (
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {pillar.topics.map((t, ti) => (
                      <Chip key={ti} label={t} size="small" />
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}

        <StepNavButtons onBack={onBack} onNext={onNext} />
      </Box>
    </Fade>
  );
}
